import { create } from "zustand";
import debounce from "lodash.debounce";
import {
  fetchPartners as apiFetchPartners,
  fetchPartnerTypes as apiFetchPartnerTypes,
  fetchPartnerProperties as apiFetchPartnerProperties,
  updatePartnerProperties as apiUpdatePartnerProperties,
  deletePartnerProperties as apiDeletePartnerProperties,
} from "../services/partnersService";

import { useErrorStore } from "components";

// Глобальний стор (state manager) для сторінки "Продукція партнерів"
export const usePartnersStore = create((set, get) => ({
  partners: [],
  partnerTypes: [],
  partnerProperties: [],
  totalCount: 0,
  isLoading: true,

  filters: {
    search: "",
    type_id: null,
    property_id: null,
    is_full: false,
    page: 1,
    page_size: 50,
    sort_by: "article",
    sort_asc: true,
  },

  debouncedRefresh: debounce(() => {
    get().fetchPartners();
  }, 20000),

  // Ініціалізує стор при першому завантаженні сторінки
  init: async () => {
    get().initFiltersFromURL();
    const typesPromise = get().fetchPartnerTypes();
    const propsPromise = get().fetchPartnerProperties();
    await get().fetchPartners();
    await Promise.all([typesPromise, propsPromise]);
  },

  // Зчитує параметри з URL і встановлює початковий стан фільтрів
  initFiltersFromURL: () => {
    const params = new URLSearchParams(window.location.search);
    set({
      filters: {
        ...get().filters,
        search: params.get("search") || "",
        type_id: params.get("type_id") || null,
        property_id: params.get("property_id") || null,
        is_full: params.get("is_full") === "true",
        page: parseInt(params.get("page") || "1", 10),
        sort_by: params.get("sort_by") || "article",
        sort_asc: params.get("sort_asc") !== "false",
      },
    });
  },

  // Оновлює URL-рядок браузера відповідно до поточного стану фільтрів
  syncFiltersToURL: () => {
    const { filters } = get();
    const params = new URLSearchParams();

    // Додаємо в URL лише значущі параметри
    if (filters.search) params.set("search", filters.search);
    if (filters.type_id) params.set("type_id", filters.type_id);
    if (filters.property_id) params.set("property_id", filters.property_id);
    if (filters.is_full) params.set("is_full", "true");
    if (filters.page > 1) params.set("page", filters.page);
    if (filters.sort_by !== "article") params.set("sort_by", filters.sort_by);
    if (!filters.sort_asc) params.set("sort_asc", "false");

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newUrl);
  },

  // Керує логікою фільтрації таблиці
  applyFilters: (newFilters) => {
    set((state) => ({
      filters: {
        ...state.filters,
        ...newFilters,
        page: 1,
      },
    }));
    get().syncFiltersToURL();
    get().fetchPartners();
  },

  // Керує логікою сортування таблиці
  setSort: (field) => {
    const { sort_by, sort_asc } = get().filters;
    if (sort_by === field) {
      set((state) => ({
        filters: { ...state.filters, sort_asc: !sort_asc },
      }));
    } else {
      set((state) => ({
        filters: { ...state.filters, sort_by: field, sort_asc: true, page: 1 },
      }));
    }
    get().syncFiltersToURL();
    get().fetchPartners();
  },

  setPage: (page) => {
    set((state) => ({
      filters: { ...state.filters, page },
    }));
    get().syncFiltersToURL();
    get().fetchPartners();
  },

  // Керує логікою пошуку
  setSearch: (value) => {
    set((state) => ({
      filters: { ...state.filters, search: value, page: 1 },
    }));
    get().syncFiltersToURL();
    get().fetchPartners();
  },

  // Керує логікою перемикання статусу is_full
  setIsFull: (isFull) => {
    set((state) => ({
      filters: { ...state.filters, is_full: isFull, page: 1 },
    }));
    get().syncFiltersToURL();
    get().fetchPartners();
  },

  // Асинхронно завантажує список продукції партнерів з сервера
  fetchPartners: async () => {
    get().debouncedRefresh.cancel();

    set({ isLoading: true });
    try {
      // Ваш API тепер не повертає `counts`, тому ми його не очікуємо
      const { data, totalCount } = await apiFetchPartners(get().filters);
      set({ partners: data, totalCount: totalCount });
    } catch (err) {
      console.error("Failed to fetch partners data:", err);
      // У випадку помилки скидаємо дані
      set({ partners: [], totalCount: 0 });
    } finally {
      set({ isLoading: false });
    }
  },

  // Асинхронно завантажує список типів продукцій партнерів з сервера
  fetchPartnerTypes: async () => {
    try {
      const types = await apiFetchPartnerTypes();
      set({ partnerTypes: types });
    } catch (err) {
      console.error("Failed to fetch partner types:", err);
      set({ partnerTypes: [] }); // У випадку помилки встановлюємо порожній масив
    }
  },

  // Асинхронно завантажує список властивостей продукцій партнерів з сервера
  fetchPartnerProperties: async () => {
    try {
      const properties = await apiFetchPartnerProperties();
      set({ partnerProperties: properties });
    } catch (err) {
      console.error("Failed to fetch partner properties:", err);
      set({ partnerProperties: [] });
    }
  },

  // Асинхронно оновлює властивості продукцій партнерів
  updatePartnerProperty: (sku, updateData) => {
    // Миттєво оновлюємо UI для кращого UX
    set((state) => ({
      partners: state.partners.map((p) =>
        p.article === sku ? { ...p, ...updateData } : p
      ),
    }));

    // У фоновому режимі надсилаємо запит на сервер
    apiUpdatePartnerProperties(sku, updateData)
      .then(() => {
        // Успіх: запускаємо відкладене повне оновлення для синхронізаці
        get().debouncedRefresh();
      })
      .catch((error) => {
        // Вивід помилки в GUI
        const { showError } = useErrorStore.getState();
        showError("Не вдалося зберегти зміни. Дані скасовано.");
        // Вивід помилки в консоль
        console.error("Failed to update partner property, rolling back:", error);

        get().fetchPartners();
      });
  },

  // Асинхронно видаляє властивості продукцій партнерів
  deletePartnerProperty: async (sku) => {
    try {
      // Викликаємо API для видалення даних на сервері
      await apiDeletePartnerProperties(sku);
      // Після успішного видалення негайно перезавантажуємо всі дані
      get().fetchPartners();
    } catch (error) {
      // Вивід помилки в GUI
      const { showError } = useErrorStore.getState();
      showError("Не вдалося видалити кастомні властивості.");
      // Вивід помилки в консоль
      console.error("Failed to delete partner property:", error);
    }
  },
}));
